import type { GenderValue, SessionCard } from '../types/taste-config'
import type { TasteMatchingCard } from '../types/taste-config'

/**
 * Генерирует UUID для сессии опроса
 */
export function generateSessionId(): string {
  // Используем crypto.randomUUID если доступен, иначе fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback для старых браузеров
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Извлекает gender из ответов пользователя (tasteData)
 * 
 * @param tasteData - Объект с ответами пользователя
 * @returns Gender value или null если не найден
 */
export function getGenderFromTasteData(tasteData: Record<string, any>): GenderValue | null {
  // Проверяем поле 'gender' напрямую
  if (tasteData.gender === 'male' || tasteData.gender === 'female') {
    return tasteData.gender
  }

  return null
}

/**
 * Маппинг gender значения для Taste Matching API
 * 
 * @param gender - Gender value из опроса ('male' | 'female')
 * @returns Gender для API запроса ('male' | 'female' | 'unisex')
 */
export function mapGenderForTasteMatching(gender: GenderValue | null): 'male' | 'female' | 'unisex' | undefined {
  if (gender === 'male') {
    return 'male'
  }
  if (gender === 'female') {
    return 'female'
  }
  // Если gender не определен, возвращаем undefined (не передаем параметр)
  return undefined
}

/**
 * Адаптирует карточку из Taste Matching API в формат для FlashCards компонента
 * 
 * @param card - Карточка из Taste Matching API
 * @returns Адаптированная карточка для UI
 */
export function adaptTasteMatchingCard(card: TasteMatchingCard): {
  id: number
  text: string
  description: string
  image: string
  originalId: string
} {
  return {
    id: card.id,
    text: card.title,
    description: card.season || '', // Используем season как описание, если есть
    image: card.imageUrl,
    originalId: card.id.toString()
  }
}

/**
 * Получает выбранные style_ids из шага "style-evaluation-choice"
 * 
 * @param tasteData - Объект с ответами пользователя
 * @returns Массив integer ID выбранных стилей
 */
export function getStyleIdsFromTasteData(
  tasteData: Record<string, any>
): number[] {
  const STEP_ID = 'style-evaluation-choice'
  const selectedStyles = tasteData[STEP_ID]

  // Если это массив, возвращаем все элементы как числа
  if (Array.isArray(selectedStyles)) {
    return selectedStyles
      .map((styleId: any) => {
        // Если уже число, возвращаем как есть
        if (typeof styleId === 'number') {
          return styleId
        }
        // Если строка, пытаемся преобразовать в число
        if (typeof styleId === 'string') {
          const num = parseInt(styleId, 10)
          return isNaN(num) ? null : num
        }
        return null
      })
      .filter((id: any) => id !== null && id !== undefined) as number[]
  }

  // Если это число (для single select), возвращаем массив с одним элементом
  if (typeof selectedStyles === 'number') {
    return [selectedStyles]
  }

  // Если это строка (старый формат), пытаемся преобразовать
  if (typeof selectedStyles === 'string' && selectedStyles.trim()) {
    const num = parseInt(selectedStyles, 10)
    return isNaN(num) ? [] : [num]
  }

  // Если ничего не выбрано, возвращаем пустой массив
  return []
}

/**
 * Адаптирует карточку из Session-based API в формат для FlashCards компонента
 * 
 * @param card - Карточка из Session-based API
 * @returns Адаптированная карточка для UI
 */
export function adaptSessionCard(card: SessionCard): {
  id: number
  text: string
  description: string
  image: string
  originalId: string
} {
  return {
    id: card.id,
    text: card.styleName || '', // Используем styleName как текст
    description: card.styleId.toString() || '', // Используем styleId (number) как описание
    image: card.imageUrl,
    originalId: card.id.toString()
  }
}


